import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useAppContext } from '../App';
import { useGeolocation } from '../hooks/useData';
import { formatRelativeTime, blurLocation, generateId, filterByTimeRange, findNearbyReport, encodeGeohash } from '../utils/location';
import { TimeFilter, PoopReport } from '../types';
import styles from './MapPage.module.css';

// Detect in-app browsers (Instagram, Facebook, TikTok, etc.)
const detectInAppBrowser = (): { isInApp: boolean; appName: string | null } => {
  const ua = navigator.userAgent || navigator.vendor || '';
  
  if (/FBAN|FBAV/i.test(ua)) {
    return { isInApp: true, appName: 'Facebook' };
  }
  if (/Instagram/i.test(ua)) {
    return { isInApp: true, appName: 'Instagram' };
  }
  if (/TikTok/i.test(ua)) {
    return { isInApp: true, appName: 'TikTok' };
  }
  if (/Twitter/i.test(ua)) {
    return { isInApp: true, appName: 'Twitter' };
  }
  if (/LinkedIn/i.test(ua)) {
    return { isInApp: true, appName: 'LinkedIn' };
  }
  
  return { isInApp: false, appName: null };
};

// Get browser name for "Open in X" button
const getBrowserName = (): string => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'Safari';
  if (/Android/i.test(ua)) return 'Chrome';
  return 'Browser';
};

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Create poop icon with different styling for active vs expired
const createPoopIcon = (isExpired: boolean) => L.divIcon({
  className: 'poop-marker',
  html: `<div style="font-size: 32px; opacity: ${isExpired ? 0.35 : 1}; filter: drop-shadow(0 2px 6px rgba(0,0,0,${isExpired ? 0.2 : 0.4})) ${isExpired ? 'grayscale(50%)' : ''};">💩</div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

// Create bag icon with different styling for active vs expired
const createBagIcon = (isExpired: boolean) => L.divIcon({
  className: 'bag-marker',
  html: `<div style="width: 32px; height: 32px; opacity: ${isExpired ? 0.35 : 1}; filter: drop-shadow(0 2px 6px rgba(0,0,0,${isExpired ? 0.2 : 0.4})) ${isExpired ? 'grayscale(50%)' : ''};"><img src="/icons/poo-bag.svg" style="width: 100%; height: 100%;" alt="bag" /></div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

// Function to get the right icon based on category
const getMarkerIcon = (category: PinCategory, isExpired: boolean) => {
  if (category === 'bagged') {
    return createBagIcon(isExpired);
  }
  return createPoopIcon(isExpired);
};

const pinIcon = L.divIcon({
  className: 'pin-marker',
  html: `<div style="font-size: 40px; filter: drop-shadow(0 3px 8px rgba(0,0,0,0.5)); animation: bounce 0.5s ease;">📍</div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 44],
});

const userLocationIcon = L.divIcon({
  className: 'user-marker',
  html: `<div style="width: 16px; height: 16px; background: #3b82f6; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 8px rgba(59,130,246,0.5);"></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

type PinCategory = 'uncollected' | 'partially-cleaned' | 'bagged';

function MapEventHandler({ 
  onLocationSelect, 
  active,
  onBoundsChange 
}: { 
  onLocationSelect: (lat: number, lng: number) => void; 
  active: boolean;
  onBoundsChange: (bounds: MapBounds) => void;
}) {
  const map = useMap();
  
  useMapEvents({
    click: (e) => {
      if (active) {
        onLocationSelect(e.latlng.lat, e.latlng.lng);
      }
    },
    moveend: () => {
      const bounds = map.getBounds();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    },
    zoomend: () => {
      const bounds = map.getBounds();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest()
      });
    }
  });

  // Initial bounds
  useEffect(() => {
    const bounds = map.getBounds();
    onBoundsChange({
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest()
    });
  }, [map, onBoundsChange]);

  return null;
}

function FlyToLocation({ location, zoom }: { location: { lat: number; lng: number } | null; zoom?: number }) {
  const map = useMap();
  useEffect(() => {
    if (location) {
      // Cap zoom at 18 to ensure tiles are available
      const safeZoom = Math.min(zoom || 18, 18);
      map.flyTo([location.lat, location.lng], safeZoom, { duration: 1 });
      
      // Trigger invalidateSize after animation completes to fix rendering
      setTimeout(() => {
        map.invalidateSize();
      }, 1100);
    }
  }, [location, map, zoom]);
  return null;
}

function MapResizeHandler() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}

// Component to capture map instance for external controls
function MapInstanceCapture({ onMapReady }: { onMapReady: (map: L.Map) => void }) {
  const map = useMap();
  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);
  return null;
}

const categoryLabels: Record<PinCategory, string> = {
  'uncollected': 'Not picked up',
  'partially-cleaned': 'Partially cleared',
  'bagged': 'Poo bag left'
};

const MapPage: React.FC = () => {
  const { poopReports, activePoopReports, settings, isAdmin, deletePoopReport, updatePoopReport, addPoopReport } = useAppContext();
  
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('ytd');
  const [flyToLocation, setFlyToLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [flyToZoom, setFlyToZoom] = useState<number>(18);
  const [mode, setMode] = useState<'view' | 'pin'>('view');
  const [selectedCategory, setSelectedCategory] = useState<PinCategory | null>(null);
  const [manualPinLocation, setManualPinLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [showGpsWarning, setShowGpsWarning] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  
  // In-app browser detection
  const [inAppBrowser] = useState(() => detectInAppBrowser());
  const [showInAppBanner, setShowInAppBanner] = useState(() => detectInAppBrowser().isInApp);
  const [showManualPinInstruction, setShowManualPinInstruction] = useState(false);

  // Dynamic footer styles
  const footerStyle = {
    background: settings.footerBgColor || '#9a3612',
  };

  const footerTextStyle = {
    color: settings.footerTextColor || '#ffffff',
  };
  
  const { location: userLocation, accuracy, requestLocation, retry, loading: locationLoading, error: locationError } = useGeolocation();

  // Check if a report is expired (not in activePoopReports OR marked as gone)
  const isReportExpired = useCallback((report: PoopReport) => {
    if (report.isGone) return true;
    return !activePoopReports.some(r => r.id === report.id);
  }, [activePoopReports]);

  // Filter by time range - use ALL reports, not just active
  const timeFilteredReports = useMemo(() => {
    return filterByTimeRange(poopReports, timeFilter);
  }, [poopReports, timeFilter]);

  // Filter by visible map bounds
  const visibleReports = useMemo(() => {
    if (!mapBounds) return timeFilteredReports;
    return timeFilteredReports.filter(report => 
      report.lat >= mapBounds.south &&
      report.lat <= mapBounds.north &&
      report.lng >= mapBounds.west &&
      report.lng <= mapBounds.east
    );
  }, [timeFilteredReports, mapBounds]);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  // Request location permission on mount
  useEffect(() => {
    requestLocation();
  }, []);

  // Fly to user location when available and map is ready (view mode only, not in in-app browser)
  useEffect(() => {
    if (userLocation && mapReady && mode === 'view' && !selectedCategory && !inAppBrowser.isInApp) {
      setFlyToZoom(16);
      setFlyToLocation(userLocation);
    }
  }, [userLocation, mapReady]);

  // Handle category button click - auto locate and zoom
  const handleCategorySelect = (category: PinCategory) => {
    setSelectedCategory(category);
    setMode('pin');
    setManualPinLocation(null); // Reset manual location, follow GPS
    
    // In-app browser: Skip GPS, go straight to manual pin mode
    if (inAppBrowser.isInApp) {
      setShowManualPinInstruction(true);
      return;
    }
    
    // Normal browser: Double GPS request for better accuracy
    requestLocation();
    setTimeout(() => {
      requestLocation();
    }, 500);
  };

  // When location arrives after category selection - fly to it (only if not manually adjusted)
  useEffect(() => {
    if (userLocation && mode === 'pin' && selectedCategory && !manualPinLocation && !inAppBrowser.isInApp) {
      setFlyToZoom(18);
      setFlyToLocation({ ...userLocation });
      
      // Check GPS accuracy
      if (accuracy && accuracy > 15) {
        setShowGpsWarning(true);
      } else {
        setShowGpsWarning(false);
      }
    }
  }, [userLocation, mode, selectedCategory, manualPinLocation, accuracy]);

  const handleCancelPin = () => {
    setMode('view');
    setSelectedCategory(null);
    setManualPinLocation(null);
    setShowGpsWarning(false);
    setShowManualPinInstruction(false);
  };

  const handleMapClick = (lat: number, lng: number) => {
    if (mode === 'pin' && selectedCategory) {
      // Check for duplicate pin within 1 meter
      const nearbyReport = findNearbyReport(lat, lng, poopReports, 1);
      if (nearbyReport) {
        setShowDuplicateError(true);
        setTimeout(() => setShowDuplicateError(false), 3000);
        return;
      }
      // User tapped to adjust - this overrides GPS tracking
      setManualPinLocation({ lat, lng });
      setShowManualPinInstruction(false); // Hide instruction after pin placed
    }
  };

  const handleMarkGone = (reportId: string) => {
    const report = poopReports.find(r => r.id === reportId);
    if (!report) return;
    
    const currentVotes = report.goneVotes || 0;
    const newVotes = currentVotes + 1;
    
    if (newVotes >= 2) {
      // 2 votes = mark as gone/expired
      updatePoopReport(reportId, { 
        goneVotes: newVotes,
        markedGoneAt: new Date().toISOString(),
        isGone: true
      });
    } else {
      updatePoopReport(reportId, { 
        goneVotes: newVotes,
        markedGoneAt: new Date().toISOString()
      });
    }
  };

  const handleConfirmReport = () => {
    // Use manual location if user tapped map, otherwise use GPS location
    const pinLocation = manualPinLocation || userLocation;
    
    if (!pinLocation || !selectedCategory) return;

    // Check for duplicate at confirm time
    const nearbyReport = findNearbyReport(pinLocation.lat, pinLocation.lng, poopReports, 1);
    if (nearbyReport) {
      setShowDuplicateError(true);
      setTimeout(() => setShowDuplicateError(false), 3000);
      return;
    }

    const blurred = blurLocation(pinLocation.lat, pinLocation.lng, settings.blurMeters);
    
    const report: PoopReport = {
      id: generateId(),
      lat: blurred.lat,
      lng: blurred.lng,
      geohash: encodeGeohash(blurred.lat, blurred.lng, 9),
      createdAt: new Date().toISOString(),
      hold: false,
      goneVotes: 0,
      isGone: false,
      category: selectedCategory
    };

    addPoopReport(report);
    setMode('view');
    setSelectedCategory(null);
    setManualPinLocation(null);
    setShowGpsWarning(false);
    setShowManualPinInstruction(false);
    setShowSuccess(true);
    
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const timeFilterLabels: Record<TimeFilter, string> = {
    'this-week': 'This Week',
    'this-month': 'This Month',
    'last-month': 'Last Month',
    'ytd': 'Year To Date',
    'last-year': 'Last Year'
  };

  return (
    <div className={styles.container}>
      {/* In-App Browser Banner */}
      {showInAppBanner && (
        <div className={styles.inAppBannerOverlay}>
          <div className={styles.inAppBanner}>
            <p className={styles.inAppBannerText}>
              GPS isn't active here. Open in external browser ({getBrowserName()}) for the best experience
            </p>
            <button 
              className={styles.inAppBannerCloseBtn}
              onClick={() => setShowInAppBanner(false)}
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}

      {/* Left Controls Panel */}
      <div className={styles.leftPanel}>
        <div className={styles.statBadge}>
          <span className={styles.statCount}>{visibleReports.length}</span>
          <div className={styles.statLabel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={styles.pinIcon}>
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#9a3612"/>
              <circle cx="12" cy="9" r="2.5" fill="white"/>
            </svg>
            <span>here</span>
          </div>
        </div>

        {/* Time Filter */}
        <button 
          className={styles.filterToggle}
          onClick={() => setShowFilters(!showFilters)}
        >
          {timeFilterLabels[timeFilter]}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>

        {/* Filter Dropdown */}
        {showFilters && (
          <div className={styles.filterDropdown}>
            {(Object.keys(timeFilterLabels) as TimeFilter[]).map(filter => (
              <button
                key={filter}
                onClick={() => { setTimeFilter(filter); setShowFilters(false); }}
                className={`${styles.filterOption} ${timeFilter === filter ? styles.filterOptionActive : ''}`}
              >
                {timeFilterLabels[filter]}
              </button>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={styles.legendIconFresh}>💩</span>
            <span>Fresh</span>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendIconFresh}><img src="/icons/poo-bag.svg" alt="bag" style={{ width: '1em', height: '1em' }} /></span>
            <span>Poo bag left</span>
          </div>
          <div className={styles.legendItem}>
            <span className={styles.legendIconCleared}>💩</span>
            <span>Cleared</span>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className={styles.zoomButtonsPanel}>
          <button 
            className={styles.zoomBtn}
            onClick={() => mapInstance?.zoomIn()}
            aria-label="Zoom in"
          >
            +
          </button>
          <button 
            className={styles.zoomBtn}
            onClick={() => mapInstance?.zoomOut()}
            aria-label="Zoom out"
          >
            −
          </button>
        </div>
      </div>

      {/* Map */}
      <div className={styles.mapWrapper}>
        <MapContainer 
          center={[51.5074, -0.1278]} 
          zoom={13} 
          className={styles.map} 
          scrollWheelZoom={true}
          zoomControl={false}
          whenReady={() => setMapReady(true)}
        >
          <TileLayer 
            attribution='&copy; OpenStreetMap' 
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapResizeHandler />
          <MapInstanceCapture onMapReady={setMapInstance} />
          <MapEventHandler 
            onLocationSelect={handleMapClick} 
            active={mode === 'pin'} 
            onBoundsChange={handleBoundsChange}
          />
          <FlyToLocation location={flyToLocation} zoom={flyToZoom} />
          
          {/* User Location Marker */}
          {userLocation && (
            <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon} />
          )}
          
          {/* Existing Reports */}
          {timeFilteredReports.map((report) => {
            const expired = isReportExpired(report);
            const reportCategory = report.category || 'uncollected';
            return (
              <Marker key={report.id} position={[report.lat, report.lng]} icon={getMarkerIcon(reportCategory, expired)}>
                <Popup className={styles.popup}>
                  <div className={styles.popupContent}>
                    {expired ? (
                      /* EXPIRED: Only show icon, time, and category */
                      <div className={styles.popupHeaderSimple}>
                        <span className={styles.popupIcon}>
                          {reportCategory === 'bagged' 
                            ? <img src="/icons/poo-bag.svg" alt="bag" style={{ width: '1.5em', height: '1.5em', verticalAlign: 'middle' }} />
                            : '💩'}
                        </span>
                        <div className={styles.popupInfo}>
                          <span className={styles.popupTime}>{formatRelativeTime(report.createdAt)}</span>
                          <span className={styles.popupCategory}>🏷️ {categoryLabels[reportCategory] || 'Not picked up'}</span>
                        </div>
                      </div>
                    ) : (
                      /* ACTIVE: Full content with "Already gone?" */
                      <>
                        <div className={styles.popupHeader}>
                          <span className={styles.popupIcon}>
                            {reportCategory === 'bagged' 
                              ? <img src="/icons/poo-bag.svg" alt="bag" style={{ width: '1.5em', height: '1.5em', verticalAlign: 'middle' }} />
                              : '💩'}
                          </span>
                          <div className={styles.popupInfo}>
                            <span className={styles.popupTime}>{formatRelativeTime(report.createdAt)}</span>
                            <span className={styles.popupCategory}>🏷️ {categoryLabels[reportCategory] || 'Not picked up'}</span>
                          </div>
                          {report.hold && <span className={styles.holdBadge}>HELD</span>}
                        </div>
                        
                        {/* Already Gone button - only for active */}
                        <button 
                          onClick={() => handleMarkGone(report.id)} 
                          className={styles.goneBtn}
                        >
                          <span>✨</span>
                          <span>Already gone?</span>
                          {report.goneVotes && report.goneVotes > 0 && (
                            <span className={styles.goneCount}>({report.goneVotes}/2)</span>
                          )}
                        </button>

                        {isAdmin && (
                          <div className={styles.adminControls}>
                            <button onClick={() => updatePoopReport(report.id, { hold: !report.hold })} className={styles.adminBtn}>
                              {report.hold ? '🔓' : '🔒'}
                            </button>
                            <button onClick={() => deletePoopReport(report.id)} className={`${styles.adminBtn} ${styles.adminBtnDanger}`}>
                              🗑️
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Pending Pin Marker - follows GPS unless user tapped to adjust */}
          {mode === 'pin' && selectedCategory && (manualPinLocation || userLocation) && (
            <Marker 
              position={[
                (manualPinLocation || userLocation)!.lat, 
                (manualPinLocation || userLocation)!.lng
              ]} 
              icon={pinIcon} 
            />
          )}
        </MapContainer>

        {/* Success Toast */}
        {showSuccess && (
          <div className={styles.toast}>
            <span>✅</span>
            <span>Poo pinned!</span>
          </div>
        )}

        {/* Duplicate Pin Error */}
        {showDuplicateError && (
          <div className={styles.duplicateToast}>
            <span>📍</span>
            <span>A pin has already been dropped here.</span>
          </div>
        )}

        {/* GPS Warning */}
        {showGpsWarning && mode === 'pin' && (
          <div className={styles.gpsWarningToast}>
            <span>⚠️</span>
            <span>Double check pin location. GPS accuracy is low here.</span>
          </div>
        )}

        {/* Pin Mode Confirm Bar - show when pin is placed */}
        {mode === 'pin' && (manualPinLocation || (userLocation && !inAppBrowser.isInApp)) && selectedCategory && (
          <div className={styles.confirmBar}>
            <span className={styles.confirmInstruction}>
              {manualPinLocation ? 'Pin adjusted' : 'Check pin location'}
            </span>
            <button 
              onClick={handleConfirmReport} 
              className={styles.confirmBtn}
            >
              Confirm ✓
            </button>
          </div>
        )}

        {/* Manual Pin Instruction (for in-app browser) */}
        {showManualPinInstruction && !manualPinLocation && (
          <div className={styles.instructionBar}>
            <span>👆</span>
            <span>Tap the map to place your pin</span>
          </div>
        )}

        {/* Pin Mode Loading - only for normal browsers */}
        {mode === 'pin' && !userLocation && locationLoading && !inAppBrowser.isInApp && (
          <div className={styles.instructionBar}>
            <span className={styles.spinnerSmall}></span>
            <span>Getting your location...</span>
          </div>
        )}
      </div>

      {/* Bottom Navigation Footer */}
      <div className={styles.bottomNav} style={footerStyle}>
        {mode === 'view' ? (
          /* Default view: Two category buttons */
          <div className={styles.footerButtons}>
            <button 
              className={styles.categoryBtn}
              onClick={() => handleCategorySelect('uncollected')}
              disabled={locationLoading}
              style={footerTextStyle}
            >
              <span>💩</span>
              <span>Not picked up</span>
            </button>
            <button 
              className={styles.categoryBtn}
              onClick={() => handleCategorySelect('bagged')}
              disabled={locationLoading}
              style={footerTextStyle}
            >
              <span><img src="/icons/poo-bag.svg" alt="bag" style={{ width: '1.25em', height: '1.25em', verticalAlign: 'middle' }} /></span>
              <span>Poo bag left</span>
            </button>
            {/* Hidden: Partially cleared - kept for future use
            <button 
              className={styles.categoryBtn}
              onClick={() => handleCategorySelect('partially-cleaned')}
              disabled={locationLoading}
              style={footerTextStyle}
            >
              <span>📍</span>
              <span>Partially cleared</span>
            </button>
            */}
          </div>
        ) : (
          /* Pin mode: Cancel button */
          <div className={styles.footerButtons}>
            <button 
              className={styles.cancelBtn}
              onClick={handleCancelPin}
              style={footerTextStyle}
            >
              <span>✕</span>
              <span>Cancel</span>
            </button>
          </div>
        )}
      </div>

      {/* Location Error - Tap to Retry (hide in in-app browsers) */}
      {locationError && !inAppBrowser.isInApp && (
        <button className={styles.errorToast} onClick={retry}>
          ⚠️ {locationError}
        </button>
      )}
    </div>
  );
};

export default MapPage;
